<template>
  <template v-if="!error">
    <TheHeaderTitle
      :title-loading="pending"
      :title="title ?? undefined"
      :title-id="titleId"
      :slug="slug"
    />

    <PageContainer>
      <NamiLoading v-if="pending"></NamiLoading>
      <NuxtPage v-else-if="title" :title-entity="title" />
    </PageContainer>
  </template>
  <ErrorPage v-else :error />
</template>

<script setup lang="ts">
import { Statistics, Title } from "~/src/api";
import { ApiToNuxtError } from "~/utils/errors/404";
import { useQuery } from "@tanstack/vue-query";

const nuxtApp = useNuxtApp();
const route = useRoute();
const { locale, t } = useI18n();
const { selectLocalizedCover } = useMediaLink();

const titleId = computed(() => route.params.id as string);
const slug = computed(() => route.params.slug as string);

const {
  data: title,
  suspense,
  isPending: pending,
  error,
} = useQuery(useTitleGet(titleId, ["cover_art", "organization", "tag"]));

onServerPrefetch(async () => {
  await suspense();
});

const rootLink = computed(() => linkTo("title", titleId.value, slug.value));

const config = useRuntimeConfig();

use404Response(ApiToNuxtError(error));

const seo = computed(() =>
  title.value
    ? {
        title: extractFromLocale(locale.value, title.value.attributes.title),
        description: extractFromLocale(
          locale.value,
          title.value.attributes.description,
        ),
        slug: title.value.attributes.slug,
        coverUrl: selectLocalizedCover(locale.value, title.value, "512"),
      }
    : {},
);

useServerSeoMeta({
  title: () => seo.value.title ?? "",
  ogTitle: () => seo.value.title,
  twitterTitle: () => seo.value.title,
  description: () => seo.value.description,
  ogDescription: () => seo.value.description,
  twitterDescription: () => seo.value.description,
  ogUrl: () => rootLink.value,
  ogLocale: () => locale.value,
  ogImage: () => seo.value.coverUrl,
  ogImageSecureUrl: () => seo.value.coverUrl,
  twitterImage: () => seo.value.coverUrl,
  ogImageHeight: "1200",
  ogImageWidth: "630",
  ogImageUrl: () => seo.value.coverUrl,
  ogImageType: "image/png",
  ogImageAlt: "NamiComi",
  ogLocaleAlternate: "en_GB",
});

useHead({
  title: () => seo.value.title ?? "Loading...",
  link: () => [
    {
      rel: "canonical",
      href: `${config.public.baseUrl}/en/title/${route.params.id}/${route.params.slug}`,
    },
  ],
  meta: () => [
    {
      property: "og:title",
      content: `${seo.value.title} - ${config.public.siteName}`,
    },
    { name: "description", content: seo.value.description },
  ],
});

// Post views only on the client - avoid doing so on the server
onMounted(async () => {
  Statistics.postView("title", route.params.id as string);
});
</script>
